import * as React from "react";
import {
  forgetRememberedAccount,
  IRememberedAccount,
  normaliseRememberedEmail,
  readRememberedAccounts
} from "../../auth/remembered-accounts";
import { SESSION_EXPIRED_REASON, SIGN_IN_REASON_PARAM } from "../../auth/sign-in-reason";
import { FireBaseService } from "../../firebase/firebase";
import { IWithHistoryProp, withHistory } from "../../shared/hoc/with-history";
import { KMainFunctions } from "../../shared/utility/main-functions";
import { TitleManager } from "../../shared/utility/title-manager";
import "../../styles/authentication.scss";
import KuikaInfoBox from "../ui-kit/kuika-info-box/kuika-info-box";
import KuikaSvg from "../ui-kit/kuika-svg/kuika-svg";
import { AccountChooser } from "./account-chooser";
import { AuthLogo } from "./auth-logo";
import { AuthenticationBanner } from "./authentication-banner";
import { isGoogleSignInAvailable } from "./google-login";
import { RememberedAccountSignIn } from "./remembered-account-sign-in";
import { SignInForm } from "./sign-in-form";

export interface ILoginProps {
  isLoginHTML?: boolean;
}

/**
 * What the left panel shows.
 * - `standard`: the plain form — no account remembered, or "Use another account".
 * - `chooser`: the accounts this device remembers.
 * - `account`: one remembered account's sign-in step.
 */
type SignInView = "standard" | "chooser" | "account";

interface ISignInScreenShownEvent {
  variant: SignInView;
  source?: string;
}

/** Straight to the account when there is exactly one, to the list when there are more. */
const getInitialView = (accounts: IRememberedAccount[]): SignInView => {
  if (accounts.length === 0) {
    return "standard";
  }
  return accounts.length === 1 ? "account" : "chooser";
};

const Login = ({ isLoginHTML = false, navigator, location }: ILoginProps & IWithHistoryProp): React.ReactElement => {
  const [accounts, setAccounts] = React.useState<IRememberedAccount[]>(readRememberedAccounts);
  const [view, setView] = React.useState<SignInView>((): SignInView => getInitialView(accounts));
  const [selectedEmail, setSelectedEmail] = React.useState<string | undefined>(
    (): string | undefined => accounts[0]?.email
  );
  const initialView: React.MutableRefObject<SignInView> = React.useRef<SignInView>(view);
  // Focus follows the person between views, but not on page load: a fresh visit keeps whatever
  // the view focuses by itself (the password of a remembered account) and nothing else.
  const [hasNavigated, setHasNavigated] = React.useState<boolean>(false);

  const isGoogleAvailable: boolean = React.useMemo(isGoogleSignInAvailable, []);
  const search: string = location?.search ?? "";
  const isSessionExpired: boolean = React.useMemo(
    (): boolean => new URLSearchParams(search).get(SIGN_IN_REASON_PARAM) === SESSION_EXPIRED_REASON,
    [search]
  );
  const isMainAI: boolean = KMainFunctions.isMainAI();

  React.useEffect((): (() => void) => {
    const source: string | null = new URLSearchParams(search).get("source");
    const event: ISignInScreenShownEvent = { variant: initialView.current };
    if (source) {
      event.source = source;
    }
    new FireBaseService().logEvent("siginin_screen_shown", event);
    TitleManager.setPageTitle("Sign in to Kuika | Low-code Application Platform", "Welcome to Main Low Code");
    TitleManager.setPageMeta(
      "Sign in to your Kuika account and start building web and mobile applications with our low-code platform."
    );
    return (): void => {
      TitleManager.clearPageTitle();
    };
    // Once per visit: the screen was shown in the view it opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedKey: string = normaliseRememberedEmail(selectedEmail ?? "");
  const selectedAccount: IRememberedAccount | undefined = accounts.find(
    (account: IRememberedAccount): boolean => normaliseRememberedEmail(account.email) === selectedKey
  );

  const pickAccount: (account: IRememberedAccount) => void = React.useCallback((account: IRememberedAccount): void => {
    setSelectedEmail(account.email);
    setView("account");
    setHasNavigated(true);
  }, []);

  const removeAccount: (account: IRememberedAccount) => void = React.useCallback(
    (account: IRememberedAccount): void => {
      const remaining: IRememberedAccount[] = forgetRememberedAccount(account.email);
      setAccounts(remaining);
      if (remaining.length === 0) {
        setView("standard");
        setHasNavigated(true);
      }
    },
    []
  );

  const useAnotherAccount: () => void = React.useCallback((): void => {
    setView("standard");
    setHasNavigated(true);
  }, []);

  const switchAccount: () => void = React.useCallback((): void => {
    setView("chooser");
    setHasNavigated(true);
  }, []);

  const backToSavedAccounts: () => void = React.useCallback((): void => {
    setHasNavigated(true);
    if (accounts.length === 1) {
      setSelectedEmail(accounts[0].email);
      setView("account");
      return;
    }
    setView("chooser");
  }, [accounts]);

  let content: React.ReactNode;
  if (view === "account" && selectedAccount) {
    content = (
      <RememberedAccountSignIn
        key={selectedAccount.email}
        account={selectedAccount}
        isLoginHTML={isLoginHTML}
        navigator={navigator}
        isGoogleAvailable={isGoogleAvailable}
        onSwitchAccount={switchAccount}
      />
    );
  } else if (view === "chooser" && accounts.length > 0) {
    content = (
      <AccountChooser
        accounts={accounts}
        onPick={pickAccount}
        onRemove={removeAccount}
        onUseAnotherAccount={useAnotherAccount}
        autoFocus={hasNavigated}
      />
    );
  } else {
    content = (
      <SignInForm
        isLoginHTML={isLoginHTML}
        navigator={navigator}
        isGoogleAvailable={isGoogleAvailable}
        onBackToSavedAccounts={accounts.length > 0 ? backToSavedAccounts : undefined}
        autoFocus={hasNavigated}
      />
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-page__left" data-testid="signInPage">
        <div className="auth-page__body">
          <div className="auth-page__logo-section">
            <AuthLogo />
          </div>

          {isSessionExpired && (
            <div role="status" data-testid="sessionExpiredNotice">
              <KuikaInfoBox
                color="blue"
                leftUnit={
                  <span className="auth-page__notice">
                    <KuikaSvg name="clock" />
                    <span>Your session expired. Sign in again to continue.</span>
                  </span>
                }
              />
            </div>
          )}

          {content}
        </div>

        {!isMainAI && (
          <nav className="auth-page__footer" aria-label="Legal">
            <a
              className="auth-page__legal-link"
              href="https://www.kuika.com/en/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy
            </a>
            <a
              className="auth-page__legal-link"
              href="https://www.kuika.com/en/legal/terms"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms
            </a>
          </nav>
        )}
      </div>

      <AuthenticationBanner />
    </div>
  );
};

const login = withHistory<ILoginProps>(Login);
export { login as Login };
